import { CapraIconsRegistry } from '@mode/capra';
import {
  getCurrentUsername,
  getLaunchDarklyClientId,
  getOwnerUsername,
  getReportOwnerUsername,
  MetaTagsService,
} from '@mode/shared/util-dom';
import { EnvironmentFacade } from '@mode/shared/util-environment';
import { FeatureFlagContext, LaunchDarklyFlagsDataService } from '@mode/shared/util-feature-flags';
import { APP_ICONS, ObservabilityEvents, ObservabilityService } from '@mode/shared/util-js';
import { first } from 'rxjs/operators';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { AccountClientService } from '@mode/shared/data-access-webapp';

async function initFlags(
  launchDarklyFlagsDataService: LaunchDarklyFlagsDataService,
  accountClient: AccountClientService,
  observabilityService: ObservabilityService,
  iconsRegistry: CapraIconsRegistry
) {
  const sdkKey = getLaunchDarklyClientId(window.document);
  const username = getCurrentUsername(window.document) || undefined; // undefined because init() expect undefined if not present
  const orgUsername = getReportOwnerUsername(window.document) || getOwnerUsername(window.document);

  // Measuring this blocking serial call to api/account#show and LD flag initialization for future optimizations
  observabilityService.mark(ObservabilityEvents.INIT_FLAGS_START);

  const [account, userAccount] = await Promise.all([
    orgUsername ? accountClient.getOrganization(orgUsername).toPromise() : Promise.resolve<null>(null),
    username ? accountClient.getAccount(username).toPromise() : Promise.resolve<null>(null),
  ]);

  if (sdkKey != null) {
    const context: FeatureFlagContext = {};

    if (userAccount != null) {
      if (userAccount.isAnonymous === false) {
        context.email = userAccount.email;
      }
      context.username = userAccount.username;
    }

    if (account != null) {
      context.billing_plan_code = account.user === true ? 'user' : account.planCode;
      context.orgName = account.username;
      context.organization = account.username;
      context.org_created_at = account.createdAt;
    }

    await launchDarklyFlagsDataService.init(sdkKey, userAccount?.username, context);
    await attachFlagsToWindow(launchDarklyFlagsDataService);
    observabilityService.recordSpanTiming(ObservabilityEvents.INIT_FLAGS, ObservabilityEvents.INIT_FLAGS_START);

    iconsRegistry.registerIcons(APP_ICONS);
  } else {
    throw new Error('Unable to get LD client key');
  }
}

async function attachFlagsToWindow(featureFlags: LaunchDarklyFlagsDataService) {
  // attach feature flags to window for use during bootstrap (i.e. route configuration)
  const flags = await featureFlags.allFlags$.pipe(first()).toPromise();
  window['MODE_FLAGS'] = flags;
}

export function appInitializer(
  featureFlags: LaunchDarklyFlagsDataService,
  accountClient: AccountClientService,
  meta: MetaTagsService,
  observabilityService: ObservabilityService,
  iconsRegistry: CapraIconsRegistry,
  environmentFacade: EnvironmentFacade
) {
  environmentFacade.init();

  meta.loadFromWindow(window);
  observabilityService.mark(ObservabilityEvents.APP_INIT, {
    startEvent: ObservabilityEvents.DOCUMENT_REQUEST,
    measure: true,
  });

  return () => initFlags(featureFlags, accountClient, observabilityService, iconsRegistry);
}
